import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import {
  SearchService,
  SearchParams,
  SearchResponse,
  searchQueryKeys,
} from '@/services/searchService';

/**
 * Configuration options for the unified search hook
 */
export interface UseUnifiedSearchOptions {
  /** Enable/disable the search query */
  enabled?: boolean;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Maximum number of results per content type */
  limit?: number;
  /** Content types to search */
  types?: Array<'product' | 'documentation'>;
  /** Language for documentation search */
  language?: string;
  /** Minimum query length before search is triggered */
  minQueryLength?: number;
}

/**
 * Return type for the unified search hook
 */
export interface UseUnifiedSearchReturn {
  /** Search results data */
  data: SearchResponse | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether the query is enabled */
  isEnabled: boolean;
  /** Whether search is actively fetching */
  isFetching: boolean;
  /** Refetch the search results */
  refetch: () => void;
  /** Whether search has been attempted */
  hasSearched: boolean;
}

/**
 * Custom hook for unified search with debouncing and caching
 *
 * This hook provides a React Query-powered search interface with:
 * - Automatic debouncing to prevent excessive API calls
 * - Intelligent caching and background updates
 * - Error handling and loading states
 * - Configurable search parameters
 *
 * @param query - Search query string
 * @param options - Search configuration options
 * @returns Search state and functions
 */
export function useUnifiedSearch(
  query: string,
  options: UseUnifiedSearchOptions = {}
): UseUnifiedSearchReturn {
  const {
    enabled = true,
    debounceMs = 300,
    limit = 10,
    types = ['product', 'documentation'],
    language = 'en',
    minQueryLength = 2,
  } = options;

  // Debounce the search query to prevent excessive API calls
  const debouncedQuery = useDebounce(query, debounceMs);

  // Determine if search should be enabled
  const isSearchEnabled = useMemo(() => {
    return enabled && debouncedQuery.length >= minQueryLength && debouncedQuery.trim() !== '';
  }, [enabled, debouncedQuery, minQueryLength]);

  // Build search parameters
  const searchParams: SearchParams = useMemo(
    () => ({
      q: debouncedQuery,
      limit,
      types,
      language,
    }),
    [debouncedQuery, limit, types, language]
  );

  // Execute search query with React Query
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: searchQueryKeys.search(searchParams),
    queryFn: () => SearchService.searchAll(searchParams),
    enabled: isSearchEnabled,
    // Stale time: consider data fresh for 30 seconds
    staleTime: 30 * 1000,
    // Cache time: keep in cache for 5 minutes
    gcTime: 5 * 60 * 1000,
    // Don't refetch on window focus for search results
    refetchOnWindowFocus: false,
    // Retry failed requests up to 1 time
    retry: 1,
    // Keep previous data while fetching new results
    placeholderData: previousData => previousData,
  });

  return {
    data,
    isLoading,
    error: error as Error | null,
    isEnabled: isSearchEnabled,
    isFetching,
    refetch,
    hasSearched: debouncedQuery.length >= minQueryLength,
  };
}

/**
 * Hook for search suggestions with debouncing
 */
export function useSearchSuggestions(
  query: string,
  options: {
    enabled?: boolean;
    debounceMs?: number;
    limit?: number;
    minQueryLength?: number;
  } = {}
) {
  const { enabled = true, debounceMs = 200, limit = 5, minQueryLength = 2 } = options;

  // Debounce the query for suggestions
  const debouncedQuery = useDebounce(query, debounceMs);

  // Determine if suggestions should be enabled
  const isEnabled = useMemo(() => {
    return enabled && debouncedQuery.length >= minQueryLength && debouncedQuery.trim() !== '';
  }, [enabled, debouncedQuery, minQueryLength]);

  return useQuery({
    queryKey: searchQueryKeys.suggestions(debouncedQuery, limit),
    queryFn: () => SearchService.getSuggestions(debouncedQuery, limit),
    enabled: isEnabled,
    staleTime: 60 * 1000, // Suggestions stay fresh for 1 minute
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false,
    retry: 0, // Don't retry failed suggestion requests
  });
}

/**
 * Hook for product-only search
 */
export function useProductSearch(
  query: string,
  options: Omit<UseUnifiedSearchOptions, 'types'> = {}
) {
  return useUnifiedSearch(query, {
    ...options,
    types: ['product'],
  });
}

/**
 * Hook for documentation-only search
 */
export function useDocumentationSearch(
  query: string,
  options: Omit<UseUnifiedSearchOptions, 'types'> = {}
) {
  return useUnifiedSearch(query, {
    ...options,
    types: ['documentation'],
  });
}
